import styled from '@emotion/styled';
import Image from 'next/image';

export const Section = styled.section`
  width: 100%;
  background: ${({ theme }) => theme.colors.neutral.white100};
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const GuideSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

export const Headline = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.neutral.grey160};
  font-size: 24px;
  font-family: Quicksand, sans-serif;
  font-weight: 700;
  line-height: 30px;

  ${({ theme }) => theme.mq('sm')} {
    font-size: 32px;
    line-height: 38px;
  }
`;

export const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  ${({ theme }) => theme.mq('sm')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.mq('lg')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const CardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  ${({ theme }) => theme.mq('sm')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.mq('lg')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const FeaturedCardWide = styled.a`
  grid-column: auto;
  min-width: 0;
  box-shadow: 0 4px 28px rgb(0 0 0 / 12%);
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.grey40};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;

  ${({ theme }) => theme.mq('sm')} {
    grid-column: span 2;
  }

  &:hover h3 {
    text-decoration: underline;
  }
`;

export const FeaturedCard = styled.a`
  min-width: 0;
  box-shadow: 0 4px 28px rgb(0 0 0 / 12%);
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.grey40};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;

  &:hover h3 {
    text-decoration: underline;
  }
`;

export const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const CardImg = styled(Image)`
  object-fit: cover;
`;

export const CardBody = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.neutral.white100};
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

export const CardTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.neutral.grey160};
  font-size: 20px;
  font-family: Quicksand, sans-serif;
  font-weight: 700;
  line-height: 24px;
`;

export const CardIntro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.neutral.grey160};
  font-size: 16px;
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 400;
  line-height: 24px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CardBottomContent = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ReadMoreLink = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.brand.green140};
  font-size: 16px;
  font-family: Quicksand, sans-serif;
  font-weight: 700;
  line-height: 24px;
`;

export const ButtonWrapper = styled.div`
  align-self: center;
`;

export const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  ${({ theme }) => theme.mq('sm')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.mq('lg')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const HiddenItemsWrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'visible',
})<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'contents' : 'none')};
`;

export const AdaptiveGrid = styled('div', {
  shouldForwardProp: prop => prop !== 'columns' && prop !== 'singleItem',
})<{ columns: number; singleItem?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  ${({ theme, columns, singleItem }) => `
    ${theme.mq('sm')} {
      grid-template-columns: repeat(${singleItem ? 1 : Math.min(columns, 2)}, 1fr);
    }
    ${theme.mq('lg')} {
      grid-template-columns: repeat(${columns}, 1fr);
    }
  `}
`;
